import { createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Delete, Lock } from "@fleet/icons";
import { cn } from "@cashsync/shared";
import { useI18n } from "@/providers/i18n-provider";
import {
  hasPinConfigured,
  PIN_MAX_ATTEMPTS,
  PIN_MAX_LENGTH,
  PIN_MIN_LENGTH,
  pinLockRemainingMs,
  recordFailedPinAttempt,
  resetPinAttempts,
  verifyPin,
} from "../pin-lock";

const KEYPAD_DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
const LOCK_TICK_MS = 250;
const MS_PER_SECOND = 1000;

/**
 * PIN lock overlay: shown on startup (once per app load)
 * if a PIN is configured, covering ALL content until unlocked.
 * 5 failed attempts → 30 s lockout (persisted in localStorage).
 *
 * Biometrics extension point: see header of `../pin-lock.ts` — a
 * "Use biometrics" button here would call the native plugin and, if validated, call `unlock()`.
 */
export function PinGate() {
  const { t } = useI18n();

  // Per page load state: if PIN is set, we start locked.
  const [unlocked, setUnlocked] = createSignal(!hasPinConfigured());
  const [entry, setEntry] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [checking, setChecking] = createSignal(false);
  const [lockRemaining, setLockRemaining] = createSignal(pinLockRemainingMs());

  // Lockout tick while gate is visible.
  createEffect(() => {
    if (unlocked()) {
      return;
    }
    const timer = setInterval(() => {
      setLockRemaining(pinLockRemainingMs());
    }, LOCK_TICK_MS);
    onCleanup(() => {
      clearInterval(timer);
    });
  });

  const isLockedOut = () => lockRemaining() > 0;

  const unlock = () => {
    resetPinAttempts();
    setEntry("");
    setError(null);
    setUnlocked(true);
  };

  const submit = async () => {
    if (checking() || isLockedOut() || entry().length < PIN_MIN_LENGTH) {
      return;
    }
    setChecking(true);
    try {
      const ok = await verifyPin(entry());
      if (ok) {
        unlock();
        return;
      }
      setEntry("");
      const state = recordFailedPinAttempt();
      if (state.lockUntil === null) {
        const remaining = PIN_MAX_ATTEMPTS - state.failures;
        setError(t(msg`Wrong PIN. ${remaining} attempts left.`));
      } else {
        setLockRemaining(pinLockRemainingMs());
        setError(t(msg`Too many attempts. Please wait.`));
      }
    } finally {
      setChecking(false);
    }
  };

  const pressDigit = (digit: string) => {
    if (isLockedOut() || checking()) {
      return;
    }
    setError(null);
    if (entry().length < PIN_MAX_LENGTH) {
      setEntry(entry() + digit);
    }
  };

  const backspace = () => {
    setEntry(entry().slice(0, -1));
  };

  return (
    <Show when={!unlocked()}>
      <div class="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-6 bg-background px-6">
        <div class="flex flex-col items-center gap-2">
          <span class="flex h-14 w-14 items-center justify-center rounded-full bg-accent/10">
            <Lock class="h-6 w-6" />
          </span>
          <h1 class="text-lg font-semibold">{t(msg`Enter your PIN`)}</h1>
          <p class="min-h-5 text-sm text-destructive">
            <Show when={isLockedOut()} fallback={error() ?? ""}>
              {(() => {
                const secs = Math.ceil(lockRemaining() / MS_PER_SECOND);
                return t(msg`Locked. Try again in ${secs}s.`);
              })()}
            </Show>
          </p>
        </div>

        {/* Indicador de dígitos */}
        <div class="flex gap-2.5" aria-label={t(msg`PIN entry`)}>
          <For each={Array.from({ length: PIN_MAX_LENGTH })}>
            {(_, index) => (
              <span
                class={cn(
                  "h-3 w-3 rounded-full border border-border transition-colors",
                  index() < entry().length ? "bg-foreground" : "bg-transparent",
                  index() >= PIN_MIN_LENGTH && index() >= entry().length && "opacity-40",
                )}
              />
            )}
          </For>
        </div>

        {/* Keypad numérico */}
        <div class="grid w-full max-w-60 grid-cols-3 gap-2">
          <For each={KEYPAD_DIGITS}>
            {(digit) => (
              <button
                type="button"
                disabled={isLockedOut()}
                onClick={() => {
                  pressDigit(digit);
                }}
                class="h-14 rounded-xl border border-border/40 text-lg font-semibold transition-colors hover:bg-accent/10 disabled:opacity-40"
              >
                {digit}
              </button>
            )}
          </For>
          <button
            type="button"
            aria-label={t(msg`Delete`)}
            disabled={isLockedOut() || entry().length === 0}
            onClick={backspace}
            class="flex h-14 items-center justify-center rounded-xl border border-border/40 transition-colors hover:bg-accent/10 disabled:opacity-40"
          >
            <Delete class="h-5 w-5" />
          </button>
          <button
            type="button"
            disabled={isLockedOut()}
            onClick={() => {
              pressDigit("0");
            }}
            class="h-14 rounded-xl border border-border/40 text-lg font-semibold transition-colors hover:bg-accent/10 disabled:opacity-40"
          >
            0
          </button>
          <button
            type="button"
            disabled={isLockedOut() || checking() || entry().length < PIN_MIN_LENGTH}
            onClick={() => void submit()}
            class="h-14 rounded-xl bg-primary text-sm font-bold text-primary-foreground transition-opacity disabled:opacity-40"
          >
            {t(msg`OK`)}
          </button>
        </div>
      </div>
    </Show>
  );
}
